import { useState, useEffect, useCallback } from "react";
import { FileText, Download, CreditCard, Shield, Car, Filter, Loader2 } from "lucide-react";
import api from "../../services/api";

var DOC_TYPE_LABELS = { boleto: "Boleto", apolice: "Apolice", consorcio_doc: "Consorcio" };
var DOC_TYPE_COLORS = { boleto: "bg-amber-100 text-amber-700", apolice: "bg-[#3B82F6]/10 text-[#3B82F6]", consorcio_doc: "bg-[#F26A4B]/10 text-[#F26A4B]" };
var DOC_TYPE_ICONS = { boleto: CreditCard, apolice: Shield, consorcio_doc: Car };

function formatCurrency(v) {
  if (!v || v === 0) return "";
  return "R$ " + Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

function formatDate(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch(e) { return ""; }
}

function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export default function ClientDocuments() {
  var docsState = useState([]);
  var docs = docsState[0];
  var setDocs = docsState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var filterState = useState("");
  var filter = filterState[0];
  var setFilter = filterState[1];
  var downloadingState = useState(null);
  var downloading = downloadingState[0];
  var setDownloading = downloadingState[1];

  var fetchDocs = useCallback(async function() {
    setLoading(true);
    try {
      var url = "/client/documents";
      if (filter) url += "?document_type=" + filter;
      var res = await api.get(url);
      setDocs(res.data.documents || []);
    } catch(err) {
      console.error("Erro ao carregar documentos:", err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(function() { fetchDocs(); }, [fetchDocs]);

  async function handleDownload(doc) {
    setDownloading(doc.id);
    try {
      var res = await api.get("/documents/" + doc.id + "/download", { responseType: "blob" });
      var url = window.URL.createObjectURL(new Blob([res.data]));
      var a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || "documento";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch(err) {
      console.error("Erro ao baixar:", err);
    } finally {
      setDownloading(null);
    }
  }

  var filters = [
    { value: "", label: "Todos" },
    { value: "boleto", label: "Boletos" },
    { value: "apolice", label: "Apolices" },
    { value: "consorcio_doc", label: "Consorcio" },
  ];

  return (
    <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm border border-gray-100 mb-6 lg:mb-8" data-testid="client-documents-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-[#3B82F6]/10 rounded-lg">
            <FileText className="w-5 h-5 text-[#3B82F6]" />
          </div>
          <div>
            <h3 className="text-base lg:text-lg font-bold text-[#0A2F3D]">Meus Documentos</h3>
            <p className="text-xs text-gray-400">{docs.length} documento{docs.length !== 1 ? "s" : ""} disponive{docs.length !== 1 ? "is" : "l"}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-3.5 h-3.5 text-gray-400" />
        {filters.map(function(f) {
          var active = filter === f.value;
          return (
            <button
              key={f.value}
              onClick={function() { setFilter(f.value); }}
              className={"px-3 py-1.5 text-xs font-medium rounded-lg transition-colors " + (active ? "bg-[#3B82F6] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200")}
              data-testid={"client-filter-" + (f.value || "all")}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : docs.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Nenhum documento disponivel</p>
        </div>
      ) : (
        <div className="space-y-3">
          {docs.map(function(doc) {
            var Icon = DOC_TYPE_ICONS[doc.document_type] || FileText;
            var typeCls = DOC_TYPE_COLORS[doc.document_type] || "bg-gray-100 text-gray-600";
            var typeLabel = DOC_TYPE_LABELS[doc.document_type] || doc.document_type;
            var bgCls = DOC_TYPE_COLORS[doc.document_type] ? DOC_TYPE_COLORS[doc.document_type].split(" ")[0] : "bg-gray-100";
            var iconCls = DOC_TYPE_COLORS[doc.document_type] ? DOC_TYPE_COLORS[doc.document_type].split(" ")[1] : "text-gray-600";

            return (
              <div key={doc.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 lg:p-4 bg-gray-50 rounded-xl border border-gray-100" data-testid={"client-doc-row-" + doc.id}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={"p-2 rounded-lg flex-shrink-0 " + bgCls}>
                    <Icon className={"w-4 h-4 " + iconCls} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0A2F3D] truncate">{doc.file_name}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-0.5">
                      <span className={"px-2 py-0.5 text-xs font-medium rounded-full " + typeCls}>{typeLabel}</span>
                      {doc.product_description && <span className="text-xs text-gray-400 truncate max-w-[200px]">{doc.product_description}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
                  {doc.value > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Valor</p>
                      <p className="text-sm font-semibold text-[#0A2F3D]">{formatCurrency(doc.value)}</p>
                    </div>
                  )}
                  {doc.due_date && (
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Vencimento</p>
                      <p className="text-sm font-medium text-[#0A2F3D]">{formatDate(doc.due_date)}</p>
                    </div>
                  )}
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-gray-400">Tamanho</p>
                    <p className="text-xs text-gray-500">{formatFileSize(doc.file_size)}</p>
                  </div>
                  <button
                    onClick={function() { handleDownload(doc); }}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-[#3B82F6] hover:bg-[#2563EB] rounded-lg transition-colors"
                    data-testid={"client-download-doc-" + doc.id}
                  >
                    {downloading === doc.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    Baixar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
